/**
 * Indexable category landing pages (pure, browser-safe config).
 * Arbitrary /rv-search filter URLs stay noindex; only these fixed paths index.
 */
export type CategorySlug =
  | "new-rvs"
  | "used-rvs"
  | "travel-trailers"
  | "motorhomes"
  | "fifth-wheels"
  | "toy-haulers"
  | "pop-up-campers"
  | "destination-trailers";

export interface CategoryConfig {
  slug: CategorySlug;
  title: string;
  h1: string;
  description: string;
  intro: string;
  condition?: "new" | "used";
  /** Exact rv_type values; empty = every type. */
  types: string[];
  searchHref: string;
  related: { label: string; href: string }[];
}

const TRAVEL = ["Travel Trailer", "Lite Travel Trailer", "Ultra-Lite Trailer"];
const MOTOR = [
  "Motorhome",
  "Motor Home Class A", "Motor Home Class A - Diesel",
  "Motor Home Class B", "Motor Home Class B - Diesel",
  "Motor Home Class C", "Motor Home Class C - Diesel",
];

export const CATEGORIES: Record<CategorySlug, CategoryConfig> = {
  "new-rvs": {
    slug: "new-rvs",
    title: "New RVs for Sale in Wilmington, NC",
    h1: "New RVs for Sale",
    description: "Shop new travel trailers, motorhomes and pop-up campers at Rex & Sons RVs in Castle Hayne near Wilmington, NC. See current prices and in-stock units.",
    intro: "Browse brand-new RVs on our lot in Castle Hayne, just north of Wilmington. Every unit below is in stock or on its way, with current pricing and full factory warranty.",
    condition: "new",
    types: [],
    searchHref: "/rv-search?condition=new",
    related: [
      { label: "Used RVs", href: "/used-rvs" },
      { label: "Travel Trailers", href: "/travel-trailers" },
      { label: "Motorhomes", href: "/motorhomes" },
      { label: "Pop-Up Campers", href: "/pop-up-campers" },
    ],
  },
  "used-rvs": {
    slug: "used-rvs",
    title: "Used RVs for Sale in Wilmington, NC",
    h1: "Used RVs for Sale",
    description: "Find quality pre-owned travel trailers, motorhomes and campers at Rex & Sons RVs near Wilmington, NC. Inspected used RVs with current prices.",
    intro: "Our pre-owned RVs are inspected by our service team before they hit the lot. Browse current used inventory below and contact us for a walkthrough.",
    condition: "used",
    types: [],
    searchHref: "/rv-search?condition=used",
    related: [
      { label: "New RVs", href: "/new-rvs" },
      { label: "Travel Trailers", href: "/travel-trailers" },
      { label: "Motorhomes", href: "/motorhomes" },
    ],
  },
  "travel-trailers": {
    slug: "travel-trailers",
    title: "Travel Trailers for Sale in Wilmington, NC",
    h1: "Travel Trailers for Sale",
    description: "Shop new and used travel trailers, including lightweight and ultra-lite models, at Rex & Sons RVs near Wilmington, NC.",
    intro: "From half-ton-towable ultra-lites to family bunkhouses, our travel trailers cover every size and budget. See the current lineup below.",
    types: TRAVEL,
    searchHref: "/rv-search?type=travel-trailer",
    related: [
      { label: "Fifth Wheels", href: "/fifth-wheels" },
      { label: "Destination Trailers", href: "/destination-trailers" },
      { label: "Pop-Up Campers", href: "/pop-up-campers" },
      { label: "Toy Haulers", href: "/toy-haulers" },
    ],
  },
  motorhomes: {
    slug: "motorhomes",
    title: "Motorhomes for Sale in Wilmington, NC",
    h1: "Motorhomes for Sale",
    description: "Shop new and used Class A, Class B and Class C motorhomes at Rex & Sons RVs near Wilmington, NC.",
    intro: "Drive-away convenience with everything on board. Browse our Class A, B and C motorhomes below, new and pre-owned.",
    types: MOTOR,
    searchHref: "/rv-search?type=motorhome",
    related: [
      { label: "New RVs", href: "/new-rvs" },
      { label: "Used RVs", href: "/used-rvs" },
      { label: "Travel Trailers", href: "/travel-trailers" },
    ],
  },
  "fifth-wheels": {
    slug: "fifth-wheels",
    title: "Fifth Wheels for Sale in Wilmington, NC",
    h1: "Fifth Wheels for Sale",
    description: "Browse fifth wheel campers for sale at Rex & Sons RVs near Wilmington, NC, with current prices and availability.",
    intro: "Fifth wheels offer extra living space and stable towing. See what is currently available below, or browse related trailer styles.",
    types: ["Fifth Wheel"],
    searchHref: "/rv-search?type=fifth-wheel",
    related: [
      { label: "Toy Haulers", href: "/toy-haulers" },
      { label: "Travel Trailers", href: "/travel-trailers" },
      { label: "Destination Trailers", href: "/destination-trailers" },
    ],
  },
  "toy-haulers": {
    slug: "toy-haulers",
    title: "Toy Haulers for Sale in Wilmington, NC",
    h1: "Toy Haulers for Sale",
    description: "Find toy hauler travel trailers and fifth wheels for sale at Rex & Sons RVs near Wilmington, NC.",
    intro: "Bring the bikes, kayaks or golf cart along. Browse current toy haulers below, or explore related trailer styles.",
    types: ["Toy Hauler Travel Trailer", "Toy Hauler Fifth Wheel"],
    searchHref: "/rv-search?type=toy-hauler",
    related: [
      { label: "Fifth Wheels", href: "/fifth-wheels" },
      { label: "Travel Trailers", href: "/travel-trailers" },
    ],
  },
  "pop-up-campers": {
    slug: "pop-up-campers",
    title: "Pop-Up Campers for Sale in Wilmington, NC",
    h1: "Pop-Up Campers for Sale",
    description: "Shop folding pop-up campers, including Rockwood Freedom models, at Rex & Sons RVs near Wilmington, NC.",
    intro: "Pop-up campers are light, easy to store and tow behind most vehicles. See our current folding campers below.",
    types: ["Folding Pop-Up Camper"],
    searchHref: "/rv-search?type=folding-pop-up-camper",
    related: [
      { label: "Travel Trailers", href: "/travel-trailers" },
      { label: "New RVs", href: "/new-rvs" },
    ],
  },
  "destination-trailers": {
    slug: "destination-trailers",
    title: "Destination Trailers for Sale in Wilmington, NC",
    h1: "Destination Trailers for Sale",
    description: "Browse destination trailers built for extended stays at Rex & Sons RVs near Wilmington, NC.",
    intro: "Destination trailers are designed for seasonal sites and long stays, with residential-style living space. See current units below.",
    types: ["Destination Trailer"],
    searchHref: "/rv-search?type=destination-trailer",
    related: [
      { label: "Travel Trailers", href: "/travel-trailers" },
      { label: "Fifth Wheels", href: "/fifth-wheels" },
    ],
  },
};

export const CATEGORY_SLUGS = Object.keys(CATEGORIES) as CategorySlug[];
export const LANDING_STATUSES = ["available", "pending", "coming_soon"];
export const LANDING_LIMIT = 24;

export interface LandingUnit {
  slug: string;
  stock_number: string;
  title: string;
  year: number | null;
  rv_type: string | null;
  condition: string | null;
  status: string;
  price: number | null;
  sale_price: number | null;
  image: string | null;
}
